import {
  ApiError,
  createSupportTicketSchema,
  MANUAL_TICKET_CHANNELS,
  type ManualTicketChannel,
  formatUsPhone,
  queryKeys,
  SUPPORT_CHANNELS,
  SUPPORT_TICKET_STATUSES,
  usPhoneSchema,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  humanizeStatus,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SUPPORT_CHANNEL_TONE,
  SUPPORT_TICKET_STATUS_TONE,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Flag, Headset, Phone, Plus, Search } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { DataTable } from '../components/data-table.js'
import { ExpandToggle, TicketNotesPreview } from '../components/notes-preview.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface SupportTicketRow {
  id: string
  ticketNumber: string
  subject: string
  channel: string
  status: string
  contactPhone: string | null
  customerName: string | null
  assignedStaffName: string | null
  urgent: boolean
  lastMessageAt: string
}

interface StaffOption {
  id: string
  name: string
  isActive: boolean
}

export const CHANNEL_LABEL: Record<string, string> = {
  sms: 'Text',
  call: 'Call',
  manual: 'Manual',
  web_chat: 'Web chat',
  contact_form: 'Contact form',
  email: 'Email',
}

export function SupportTicketsPage() {
  const navigate = useNavigate()
  const { can, staff: me } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<string>('open')
  const [urgentOnly, setUrgentOnly] = useState(false)
  const [channelFilter, setChannelFilter] = useState('all')
  // 'all' | 'me' | 'unassigned' | a staff id
  const [assignedFilter, setAssignedFilter] = useState('all')
  const [openedFrom, setOpenedFrom] = useState('')
  const [openedTo, setOpenedTo] = useState('')
  const [creating, setCreating] = useState(false)
  const [sort, setSort] = useState<{ column: string; order: 'asc' | 'desc' }>({
    column: 'lastMessageAt',
    order: 'desc',
  })
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggleExpanded = useCallback(
    (id: string) =>
      setExpanded((prev) => {
        const next = new Set(prev)
        if (!next.delete(id)) next.add(id)
        return next
      }),
    [],
  )

  const hasFilters =
    Boolean(search) ||
    status !== 'open' ||
    urgentOnly ||
    channelFilter !== 'all' ||
    assignedFilter !== 'all' ||
    Boolean(openedFrom) ||
    Boolean(openedTo)
  const clearFilters = () => {
    setSearch('')
    setStatus('open')
    setUrgentOnly(false)
    setChannelFilter('all')
    setAssignedFilter('all')
    setOpenedFrom('')
    setOpenedTo('')
  }

  const canSeeStaff = can('staff:read')
  const { data: staffList } = useQuery({
    queryKey: queryKeys.staff.list(),
    queryFn: () => api.get<StaffOption[]>('/staff'),
    enabled: canSeeStaff,
  })

  // Local-day boundaries: "to" is inclusive, so send the start of the next day.
  const fromIso = openedFrom ? new Date(`${openedFrom}T00:00:00`).toISOString() : undefined
  const toIso = openedTo
    ? new Date(new Date(`${openedTo}T00:00:00`).getTime() + 86_400_000).toISOString()
    : undefined

  const filters = {
    q: search || undefined,
    status: status === 'all' ? undefined : status,
    urgent: urgentOnly ? true : undefined,
    channel: channelFilter === 'all' ? undefined : channelFilter,
    assignedStaffId:
      assignedFilter === 'me'
        ? (me?.id ?? undefined)
        : assignedFilter === 'all' || assignedFilter === 'unassigned'
          ? undefined
          : assignedFilter,
    unassigned: assignedFilter === 'unassigned' ? true : undefined,
    createdFrom: fromIso,
    createdTo: toIso,
    sort: sort.column,
    order: sort.order,
    pageSize: 50,
  }

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.supportTickets.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: SupportTicketRow[]; total: number; page: number; pageSize: number }>(
        '/support-tickets',
        { query: { ...filters, page: pageParam } },
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    // The queue should feel live without anyone refreshing — same interval
    // machines.tsx and provisioning.tsx poll at. Re-fetches every loaded page.
    refetchInterval: 15_000,
  })
  const rows = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const setUrgent = useMutation({
    mutationFn: ({ id, urgent }: { id: string; urgent: boolean }) =>
      api.patch(`/support-tickets/${id}`, { urgent }),
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() })
      toast.success(vars.urgent ? 'Marked urgent' : 'Urgent mark removed')
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })
  const canWrite = can('support:write')

  const columns = useMemo<ColumnDef<SupportTicketRow, unknown>[]>(
    () => [
      {
        header: 'Ticket',
        accessorKey: 'ticketNumber',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <ExpandToggle
              expanded={expanded.has(row.original.id)}
              onToggle={() => toggleExpanded(row.original.id)}
              label={row.original.ticketNumber}
            />
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 font-identifier font-medium">
                {row.original.ticketNumber}
                {row.original.urgent ? (
                  <Badge tone="danger">
                    <Flag className="me-0.5 size-3" aria-hidden="true" /> Urgent
                  </Badge>
                ) : null}
              </p>
              <p className="truncate text-xs text-foreground-muted">{row.original.subject}</p>
            </div>
          </div>
        ),
      },
      {
        header: 'Channel',
        accessorKey: 'channel',
        cell: ({ row }) => (
          <Badge tone={SUPPORT_CHANNEL_TONE[row.original.channel] ?? 'neutral'}>
            {CHANNEL_LABEL[row.original.channel] ?? row.original.channel}
          </Badge>
        ),
      },
      {
        header: 'Contact',
        accessorKey: 'contactPhone',
        cell: ({ row }) => (
          <div>
            {row.original.customerName ? <p>{row.original.customerName}</p> : null}
            <p className="font-identifier text-xs text-foreground-muted">
              {row.original.contactPhone ? formatUsPhone(row.original.contactPhone) : '—'}
            </p>
          </div>
        ),
      },
      {
        header: 'Assigned',
        accessorKey: 'assignedStaffName',
        meta: { sortKey: 'assignedStaffName' },
        cell: ({ row }) => (
          <span className="text-foreground-muted">{row.original.assignedStaffName ?? '—'}</span>
        ),
      },
      {
        header: 'Last activity',
        accessorKey: 'lastMessageAt',
        meta: { sortKey: 'lastMessageAt' },
        cell: ({ row }) => (
          <span className="text-xs text-foreground-muted">
            {new Date(row.original.lastMessageAt).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </span>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => (
          <Badge tone={SUPPORT_TICKET_STATUS_TONE[row.original.status] ?? 'neutral'}>
            {humanizeStatus(row.original.status)}
          </Badge>
        ),
      },
      ...(canWrite
        ? [
            {
              header: 'Urgent',
              id: 'urgent',
              cell: ({ row }: { row: { original: SupportTicketRow } }) => (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-pressed={row.original.urgent}
                  aria-label={
                    row.original.urgent
                      ? `Remove urgent mark from ${row.original.ticketNumber}`
                      : `Mark ${row.original.ticketNumber} urgent`
                  }
                  disabled={setUrgent.isPending}
                  onClick={(e) => {
                    e.stopPropagation()
                    setUrgent.mutate({ id: row.original.id, urgent: !row.original.urgent })
                  }}
                >
                  <Flag
                    className={
                      row.original.urgent
                        ? 'size-4 fill-current text-danger'
                        : 'size-4 text-foreground-subtle'
                    }
                    aria-hidden="true"
                  />
                </Button>
              ),
            } as ColumnDef<SupportTicketRow, unknown>,
          ]
        : []),
    ],
    [expanded, toggleExpanded, canWrite, setUrgent],
  )

  return (
    <Page>
      <PageHeader
        title="Support"
        description="Inbound calls and texts from customers, tracked as tickets."
        actions={
          can('support:write') ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus /> New ticket
            </Button>
          ) : null
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search
            className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground-subtle"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ticket number, phone, subject…"
            className="ps-8"
          />
        </div>
        <div className="w-44">
          <Select value={status} onValueChange={(v) => setStatus(v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {SUPPORT_TICKET_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {humanizeStatus(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Select
            value={urgentOnly ? 'urgent' : 'all'}
            onValueChange={(v) => setUrgentOnly(v === 'urgent')}
          >
            <SelectTrigger aria-label="Urgent filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tickets</SelectItem>
              <SelectItem value="urgent">Urgent only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger aria-label="Channel filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              {SUPPORT_CHANNELS.map((c) => (
                <SelectItem key={c} value={c}>
                  {CHANNEL_LABEL[c] ?? c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <Select value={assignedFilter} onValueChange={setAssignedFilter}>
            <SelectTrigger aria-label="Assigned filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Anyone</SelectItem>
              {me ? <SelectItem value="me">Assigned to me</SelectItem> : null}
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {(staffList ?? [])
                .filter((s) => s.isActive && s.id !== me?.id)
                .map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-foreground-muted">
          Opened
          <Input
            type="date"
            aria-label="Opened from"
            className="w-36"
            value={openedFrom}
            max={openedTo || undefined}
            onChange={(e) => setOpenedFrom(e.target.value)}
          />
          –
          <Input
            type="date"
            aria-label="Opened to"
            className="w-36"
            value={openedTo}
            min={openedFrom || undefined}
            onChange={(e) => setOpenedTo(e.target.value)}
          />
        </label>
        {hasFilters ? (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        ) : null}
      </div>

      <DataTable
        data={rows}
        columns={columns}
        isLoading={isLoading}
        sort={{
          column: sort.column,
          order: sort.order,
          onChange: (column, order) => setSort({ column, order }),
        }}
        renderExpanded={(row) =>
          expanded.has(row.id) ? <TicketNotesPreview ticketId={row.id} /> : null
        }
        onRowClick={(row) =>
          void navigate({ to: '/support-tickets/$ticketId', params: { ticketId: row.id } })
        }
        infinite={{
          total,
          hasMore: Boolean(hasNextPage),
          isFetchingMore: isFetchingNextPage,
          onLoadMore: () => void fetchNextPage(),
        }}
        empty={
          hasFilters ? (
            <EmptyState
              icon={Search}
              variant="no-results"
              title="No tickets match"
              description="Try different filters or a different search."
              onClearFilters={clearFilters}
            />
          ) : (
            <EmptyState
              icon={Headset}
              title="No open tickets"
              description="Inbound texts and calls to your Twilio number show up here automatically."
            />
          )
        }
      />

      <NewTicketDialog open={creating} onOpenChange={setCreating} />
    </Page>
  )
}

interface CustomerOption {
  id: string
  name: string
  phone: string | null
}

/** Opening a ticket by hand — a call taken on a personal phone, a customer
 *  who stopped by the counter. Nothing is required but a subject; a phone
 *  can be added later once one is known. A customer can be picked up front
 *  (t1218) — or preset by the customer page — so the ticket shows on their
 *  record. A typed phone that belongs to a customer is only suggested, never
 *  linked on its own: the number can belong to the wrong person (see
 *  `LinkCustomerDialog` in support-ticket-detail.tsx). */
export function NewTicketDialog({
  open,
  onOpenChange,
  customer: presetCustomer,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  customer?: { id: string; name: string }
}) {
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { can } = useAuth()
  const canPickCustomer = can('customers:read')
  const [subject, setSubject] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [channel, setChannel] = useState<ManualTicketChannel>('manual')
  const [contactEmail, setContactEmail] = useState('')
  const [note, setNote] = useState('')
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null)
  const [customerSearch, setCustomerSearch] = useState('')

  const customer = picked ?? presetCustomer ?? null

  const reset = () => {
    setSubject('')
    setContactPhone('')
    setChannel('manual')
    setContactEmail('')
    setNote('')
    setPicked(null)
    setCustomerSearch('')
  }

  const parsedPhone = usPhoneSchema.safeParse(contactPhone.trim())
  const matchPhone = parsedPhone.success ? parsedPhone.data : ''

  const { data: phoneMatch } = useQuery({
    queryKey: queryKeys.customers.match(matchPhone),
    queryFn: () =>
      api.get<{ customer: CustomerOption | null }>('/customers/match', {
        query: { phone: matchPhone },
      }),
    enabled: open && canPickCustomer && !customer && Boolean(matchPhone),
  })
  const suggestion = !customer ? (phoneMatch?.customer ?? null) : null

  const { data: searchResults } = useQuery({
    queryKey: queryKeys.customers.list({ q: customerSearch }),
    queryFn: () =>
      api.get<{ items: CustomerOption[] }>('/customers', {
        query: { q: customerSearch, pageSize: 6 },
      }),
    enabled: open && canPickCustomer && !customer && customerSearch.trim().length >= 2,
  })

  const create = useMutation({
    mutationFn: async () => {
      const parsed = createSupportTicketSchema.safeParse({
        subject,
        channel,
        contactPhone: contactPhone.trim() || null,
        contactEmail: contactEmail.trim() || null,
        customerId: customer?.id ?? null,
        note: note.trim() || null,
      })
      if (!parsed.success) {
        throw new Error(parsed.error.issues[0]?.message ?? 'Check the form and try again')
      }
      return api.post<{ id: string }>('/support-tickets', parsed.data)
    },
    onSuccess: async (ticket) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() })
      toast.success('Ticket opened')
      onOpenChange(false)
      reset()
      await navigate({ to: '/support-tickets/$ticketId', params: { ticketId: ticket.id } })
    },
    onError: (error: Error) =>
      toast.error(
        'Could not open the ticket',
        error instanceof ApiError ? error.message : error.message,
      ),
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Phone className="me-1.5 inline size-4" aria-hidden="true" /> New ticket
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Subject" required>
            {(props) => (
              <Input {...props} value={subject} onChange={(e) => setSubject(e.target.value)} />
            )}
          </Field>
          <Field label="How did they reach us?" hint="Defaults to Manual.">
            {(props) => (
              <Select value={channel} onValueChange={(v) => setChannel(v as ManualTicketChannel)}>
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MANUAL_TICKET_CHANNELS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {CHANNEL_LABEL[c] ?? c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          {channel === 'email' ? (
            <Field label="Contact email" required hint="Replies on this ticket go out by email.">
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              )}
            </Field>
          ) : null}
          <Field label="Contact phone" hint="If known — lets a reply be sent by text.">
            {(props) => (
              <Input
                {...props}
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
              />
            )}
          </Field>
          {canPickCustomer || customer ? (
            <Field label="Customer" hint="Optional — the ticket then shows on their page.">
              {(props) =>
                customer ? (
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="font-medium">{customer.name}</span>
                    {picked ? (
                      <Button variant="ghost" size="sm" onClick={() => setPicked(null)}>
                        Change
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input
                      {...props}
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      placeholder="Search name or phone…"
                    />
                    {suggestion ? (
                      <Button variant="secondary" size="sm" onClick={() => setPicked(suggestion)}>
                        This number belongs to {suggestion.name} — link
                      </Button>
                    ) : null}
                    {searchResults?.items.length ? (
                      <ul className="divide-y divide-border">
                        {searchResults.items.map((row) => (
                          <li key={row.id}>
                            <button
                              type="button"
                              className="w-full px-1 py-2 text-start text-sm hover:bg-surface-sunken"
                              onClick={() => setPicked({ id: row.id, name: row.name })}
                            >
                              <span className="block font-medium">{row.name}</span>
                              <span className="block font-identifier text-2xs text-foreground-muted">
                                {row.phone ? formatUsPhone(row.phone) : '—'}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                )
              }
            </Field>
          ) : null}
          <Field label="Note">
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => create.mutate()}
            disabled={
              !subject.trim() || (channel === 'email' && !contactEmail.trim()) || create.isPending
            }
          >
            {create.isPending ? 'Opening…' : 'Open ticket'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
